import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useTranslation } from "react-i18next";

const articles = [
  {
    date: "March 28, 2025",
    category: "Market Update",
    title: "Brent Crude Surges Amid Middle East Supply Concerns",
    excerpt:
      "Global crude oil prices climbed sharply this week as geopolitical tensions in the Middle East raised concerns about potential supply disruptions. Medigreen analysts assess the impact on procurement strategies for Asian buyers.",
    img: "https://public.readdy.ai/ai/img_res/8390c67c1d7f3cbf5242c9785843cb2d.jpg",
  },
  {
    date: "March 15, 2025",
    category: "LNG Insights",
    title: "Asia-Pacific LNG Demand Outlook for Q2 2025",
    excerpt:
      "Demand for LNG across Asia-Pacific markets is expected to remain robust through Q2 2025, driven by industrial recovery in Japan and South Korea and continued growth in Southeast Asian markets.",
    img: "https://readdy.ai/api/search-image?query=LNG%20carrier%20ship%20at%20night%20with%20illuminated%20port%20facilities%2C%20Asia%20Pacific%20shipping%20route%2C%20industrial%20maritime%20photography&width=600&height=400&seq=n002&orientation=landscape",
  },
  {
    date: "March 5, 2025",
    category: "Company News",
    title: "Medigreen Expands Advisory Services to Southeast Asia",
    excerpt:
      "Medigreen LLC announces the expansion of its procurement advisory services to Southeast Asian markets, establishing new partnerships with regional energy companies in Singapore and Malaysia.",
    img: "https://readdy.ai/api/search-image?query=Singapore%20skyline%20with%20energy%20infrastructure%20and%20port%20facilities%2C%20Southeast%20Asia%20business%20hub%2C%20modern%20city%20with%20industrial%20waterfront&width=600&height=400&seq=n003&orientation=landscape",
  },
  {
    date: "February 20, 2025",
    category: "Market Update",
    title: "OPEC+ Production Decision Impact on Asian Crude Buyers",
    excerpt:
      "The latest OPEC+ production cut extension has significant implications for Asian crude oil buyers. We analyze the expected price impact and recommend procurement strategies to mitigate cost increases.",
    img: "https://readdy.ai/api/search-image?query=OPEC%20oil%20production%20facility%20in%20Middle%20East%20desert%2C%20large%20oil%20field%20with%20pumping%20equipment%2C%20professional%20industrial%20photography%2C%20dramatic%20sky&width=600&height=400&seq=n004&orientation=landscape",
  },
  {
    date: "February 8, 2025",
    category: "LNG Insights",
    title: "US LNG Export Capacity Expansion: Opportunities for Asian Buyers",
    excerpt:
      "New US LNG export terminals coming online in 2025-2026 present significant opportunities for Asian buyers seeking supply diversification. Medigreen evaluates the commercial and logistical considerations.",
    img: "https://readdy.ai/api/search-image?query=US%20LNG%20export%20terminal%20with%20loading%20facilities%20and%20tanker%20ships%2C%20American%20energy%20infrastructure%2C%20professional%20industrial%20photography%2C%20coastal%20setting&width=600&height=400&seq=n005&orientation=landscape",
  },
  {
    date: "January 25, 2025",
    category: "Company News",
    title: "Medigreen Publishes Annual Energy Procurement Outlook 2025",
    excerpt:
      "Our comprehensive annual report covers key trends shaping crude oil and LNG markets in 2025, including supply-demand dynamics, price forecasts, and strategic recommendations for energy buyers.",
    img: "https://readdy.ai/api/search-image?query=professional%20energy%20market%20research%20report%20publication%2C%20business%20analytics%20document%20with%20charts%20and%20graphs%2C%20corporate%20office%20setting%2C%20clean%20modern%20design&width=600&height=400&seq=n006&orientation=landscape",
  },
];

export default function NewsPage() {
  const { t } = useTranslation(["pages"]);

  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="relative pt-32 pb-20 bg-slate-900">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=energy%20news%20media%20broadcast%20studio%20with%20global%20energy%20market%20data%20screens%2C%20professional%20journalism%20environment%2C%20modern%20technology&width=1920&height=600&seq=newshero&orientation=landscape"
            alt="News"
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <p className="text-emerald-400 text-sm font-semibold uppercase tracking-widest mb-4">
            {t("pages:news.hero.subtitle")}
          </p>
          <h1
            className="text-5xl md:text-6xl font-bold text-white mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("pages:news.hero.title")}
          </h1>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto">
            {t("pages:news.hero.description")}
          </p>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((a) => (
              <article
                key={a.title}
                className="group cursor-pointer border border-gray-100 rounded-xl overflow-hidden hover:border-emerald-200 transition-all duration-300"
              >
                <div className="w-full h-52 overflow-hidden">
                  <img
                    src={a.img}
                    alt={a.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="bg-emerald-50 text-emerald-700 text-xs font-semibold px-3 py-1 rounded-full">
                      {a.category}
                    </span>
                    <span className="text-slate-400 text-xs">{a.date}</span>
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2 group-hover:text-emerald-700 transition-colors leading-snug">
                    {a.title}
                  </h3>
                  <p className="text-slate-500 text-sm leading-relaxed line-clamp-3">{a.excerpt}</p>
                  <div className="flex items-center gap-1 mt-4 text-emerald-700 text-sm font-semibold">
                    {t("pages:news.articles.readMore")} <i className="ri-arrow-right-line" />
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}